import React, { useEffect, useState } from 'react';
import { ControlledList, DataGridLayout } from 'framework/list';
import { DateField } from 'framework/ui/field';
import { FormFieldsData, ListRowSelectCheckbox } from 'framework/form';
import { Box, Typography } from '@mui/material';
import { getWithThousandsSeparator } from 'framework/utils';
import { Loading } from 'framework/ui/mui-builder';
import { useSensitiveDataContext } from 'framework/sensitiveData';
import ReconciliationAmountField from 'domain/finance/costAccounting/reconcilation/shared/field/ReconciliationAmountField';

export const calculateTotal = (items: number[]) => items?.reduce((acc: number, item: number) => acc + (item || 0), 0);

const ReconciliationTransfersList = ({
  label,
  onRowSelection,
  items,
  loading,
  children,
  dateSource,
  amountSource,
  idSource,
  isSensitiveDataIsShown,
  setTotalSelectedBankTransaction,
  totalSelectedBankTransaction,
}: {
  label: string;
  dateSource: string;
  amountSource: string;
  idSource: string;
  items: Dictionary<any>[];
  loading: boolean;
  onRowSelection: (ids: string[]) => void;
  children?: React.ReactElement | React.ReactElement[];
  isSensitiveDataIsShown?: boolean;
  setTotalSelectedBankTransaction?: React.Dispatch<React.SetStateAction<number | undefined>>;
  totalSelectedBankTransaction?: number;
}) => {
  const [checkedRowIds, setCheckedRowIds] = useState<string[]>([]);
  const sensitiveDataContext = useSensitiveDataContext();

  const getAmounts = (data: Dictionary<any>[]) =>
    data.map(item => sensitiveDataContext.getValueByIdAndSource(amountSource, item[idSource]) || 0) || [];

  const total = calculateTotal(getAmounts(items));
  const totalSelected = calculateTotal(
    getAmounts(items?.filter((item: Dictionary<any>) => checkedRowIds.includes(item[idSource])) || []),
  );

  const handleRowSelection = ({ checked, ...values }: FormFieldsData) => {
    const id = values[idSource];

    if (checked) {
      setCheckedRowIds(prev => [...prev, id]);
    } else {
      setCheckedRowIds(prev => prev.filter(item => item !== id));
    }
  };

  const calculatePercentage = (totalSelectedDavinci: number, totalSelectedValue: number) => {
    if (totalSelectedDavinci === 0) {
      return 0;
    }
    const percentage = (totalSelectedValue / totalSelectedDavinci) * 100;
    return Math.round(percentage);
  };

  useEffect(() => {
    onRowSelection(checkedRowIds);
    // Suppressed warnings because we only need to call useEffect callback if checkedRowIds is changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedRowIds]);

  useEffect(() => {
    if (setTotalSelectedBankTransaction) {
      setTotalSelectedBankTransaction(totalSelected);
    }
  }, [totalSelected, setTotalSelectedBankTransaction]);

  useEffect(() => {
    if (items) {
      setCheckedRowIds([]);
    }
  }, [items]);

  return (
    <ControlledList
      data={{
        items: items || [],
        totalElements: items?.length || 0,
        totalPages: 1,
      }}
      loading={loading}
    >
      <Box sx={{ width: '100%', '.MuiCheckbox-root': { p: '2px' }, 'th:first-child, td: first-child': { pr: 0 } }}>
        <Typography variant="h4" color="primary">
          {label}
        </Typography>
        {loading ? (
          <Box sx={{ position: 'relative', minHeight: '50px', width: '100%', mt: 1 }}>
            <Loading />
          </Box>
        ) : (
          <>
            <Box sx={{ maxHeight: '550px', overflowY: 'auto' }}>
              <DataGridLayout isSmallTable showTotal={false} sxPaginationWrapper={{ display: 'none' }}>
                {
                  [
                    <ListRowSelectCheckbox
                      getDataSource={idSource}
                      source="checkbox"
                      label=""
                      onChangeCallback={handleRowSelection}
                      width="40px"
                    />,
                    <DateField source={dateSource} label="Date" width={105} />,
                    isSensitiveDataIsShown ? (
                      <ReconciliationAmountField source={amountSource} idSource={idSource} label="Amount" width={130} />
                    ) : null,
                    children,
                  ].filter(Boolean) as React.ReactElement[]
                }
              </DataGridLayout>
            </Box>
            {isSensitiveDataIsShown && items.length ? (
              <Typography component="div" variant="subtitle1" color="textPrimary" mt={2}>
                Total: {getWithThousandsSeparator(total)}$
              </Typography>
            ) : null}
            {isSensitiveDataIsShown && items.length && checkedRowIds.length ? (
              <Typography component="div" variant="subtitle1" color="textPrimary" mt={0}>
                Total for selected items: {getWithThousandsSeparator(totalSelected)}${' '}
                {totalSelectedBankTransaction
                  ? `(${calculatePercentage(totalSelectedBankTransaction, totalSelected)}%)`
                  : ''}
              </Typography>
            ) : null}
          </>
        )}
      </Box>
    </ControlledList>
  );
};

export default ReconciliationTransfersList;
